<script lang="ts">
import { onDestroy } from "svelte";
import { derived, get } from "svelte/store";
import View from "ol/View";
import { EXTENT as WEB_MERCATOR_EXTENT } from "ol/proj/epsg3857";
// The catalogues and the language in use, set up before anything renders.
import "./locale/i18n";
import { _ } from "svelte-i18n";

import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import { fromLonLat, toLonLat, transformExtent } from "ol/proj";
import Map from "./components/Map.svelte";
import MapLoading from "./components/MapLoading.svelte";
import Lazy from "./components/Lazy.svelte";
import Guide3D from "./components/Guide3D.svelte";
import PeelSlider from "./components/PeelSlider.svelte";
import Logo from "./components/Logo.svelte";
import NowcastPlayback from "./components/NowcastPlayback.svelte";
import BottomToolbar from "./components/BottomToolbar.svelte";

import RadarCapability from "./caps/RadarCapability";

import { LayerManager } from "./lib/LayerManager";
import { capabilityEnabled } from "./caps/enabled";
import { progress } from "./lib/progress";
import Settings from "./lib/Settings";
import type { SettingValue } from "./lib/Settings";
import { resolveBaseLayer as resolveSettingBaseLayer } from "./lib/baseLayers";

import { tileRefreshSignal } from "./stores";
import {
  bottomToolbarMode,
  colorSchemeDark,
  cellLayerVisible, cells3dFailed, cells3dLoading, cells3dVisible, cycloneLayerVisible, lastFocus, observedProduct,
  layerswitcherVisible,
  capLatestObservation, capTimeIndicator, cutRotationDeg, stormDetails,
  lightningLayerVisible, logoStyle,
  mapBaseLayer, mapExtent4326, modelCompareAt, networkStatus, playbackRunning, browsingFrames, precacheForecast, radarColormap,
  radarColorScheme, selectedStorm, sharedActiveCap, smallScreen, snowLayerVisible, terrain3dVisible,
  toolbarVisible, fullResolution3d, unit, playbackFromStart, radarExplainerOpen, meteocoolNowcast,
  siteMarksVisible, siteMode, srvManualMotion, zoomlevel,
} from "./stores";
import { DEFAULT_MANUAL_MOTION, formatManualMotion, parseManualMotion, sameManualMotion } from "./lib/srvMotion";

import "./global.css";
import "@shoelace-style/shoelace/dist/themes/light.css";
// Scoped to .sl-theme-dark, which ui.ts toggles from the colorSchemeDark store.
import "@shoelace-style/shoelace/dist/themes/dark.css";
// Last on purpose: it re-points Shoelace's panel, overlay and primary tokens.
import "./glass.css";
import { websocketBaseUrl } from "./urls";
import { onWake, wake, whenVisible, wokeWithin } from "./lib/wakeup";
import { retryFailedTiles } from "./lib/tileStatus";
import { installScrollbars } from "./lib/scrollbars";
import { fetchCurrentVolumes, fetchLightningCache, fetchMesocyclones, quietly } from "./api";
import type { CurrentVolumes, Progress, RadarVolume } from "./api";
import { showsLatestFrame } from "./lib/freshness";
import { DEFAULT_PRODUCT, parseObservedProduct } from "./lib/observedProduct";
import { nextSelection } from "./lib/cellSelection";
import { applyLinkedOverlays, openingLink, startUrlState } from "./lib/urlState";
import { exposeShareLink } from "./lib/share";
import { applyScreenshotLook, isScreenshot, markScreenshotReady, SCREENSHOT_CLASS, whenDrawn } from "./lib/screenshot";
import { setElementCentre } from "./lib/viewCentre";
import type { ClientToServerEvents, ServerToClientEvents } from "./api/events";
import { cleanupUIConstants, initUIConstants, setSolidGlassWhileMoving } from "./layers/ui";
import { watchForSlowDevice } from "./lib/slowDevice";
import makeLightningLayer from "./layers/lightning";
import StrikeManager, { LIVE_STRIKE_BATCH_MS } from "./lib/StrikeManager";
import { coalesce } from "./lib/coalesce";
import MesoCycloneManager from "./lib/MesoCycloneManager";
import StormManager from "./lib/StormManager";
import { offerVolumes, startStormResolver } from "./lib/stormResolver";
import { existedBy, isResolution, selectionKey, withStorm } from "./lib/stormSelection";

import makeMesocycloneLayer from "./layers/mesocyclones";
import makeCellLayer from "./layers/cells";
import makeCellPulseLayer from "./layers/cellPulse";
import makeCloudHints from "./layers/cloudHints";
import makeRadarSiteMarks, { MIN_ZOOM as SITE_MARKS_MIN_ZOOM } from "./layers/radarSiteMarks";
import makeSiteSweepLayer from "./layers/siteSweep";
import makeBeamRings from "./layers/beamRings";
import SiteSweepManager, { readThrough } from "./lib/SiteSweepManager";
import { NETWORKS } from "./layers/network";
import { dwdAttribution } from "./layers/attributions";
import { VolumeFeed } from "./lib/scans";
import { forget3DOrigin, openCloudIn3D, origin3D, registerOpen3D, returnFrom3D } from "./lib/open3d";
/* The storm panel is loaded when a storm is opened, not with the page: the
   history charts, the lineage graph (dagre) and the two 3D pictures are a
   third of the app's code, and most sessions never tap a storm. */
const loadStormDetails = () => import("./components/StormDetails.svelte");
const loadCellSheet = () => import("./components/CellSheet.svelte");
const loadRadarExplainer = () => import("./components/RadarExplainer.svelte");
// The model comparison too: its charts and the forecast fetch are for the
// few who open it.
const loadModelCompare = () => import("./components/ModelCompare.svelte");
const loadStormSheet = () => Promise.all([loadCellSheet(), loadStormDetails()]);
const loadCompare = (sheet: boolean) => () => Promise.all([loadModelCompare(), sheet ? loadCellSheet() : null]);
import StormSelectionHint from "./components/StormSelectionHint.svelte";
import PointMenu from "./components/PointMenu.svelte";
import { DeviceDetect as dd } from "./lib/DeviceDetect";
import { labelsOnly } from "./layers/vector";
import PrecipitationTypesCapability from "./caps/PrecipitationTypesCapability";
import Cells3DCapability from "./caps/Cells3DCapability";
import { radolanOverlay } from "./layers/dwd";
import { webglSupported } from "./layers/webglTile";
import { reportNotice } from "./lib/Toast";
import { dropEarlierFrames, forgetEarlierFrames } from "./layers/valueTiles";
import { isHandheld } from "./lib/gpuBudget";
import LightningCapability from "./caps/LightningCapability";

export let device;
export let postInitCb;

dd.set(device);

// The native wrappers need CSS hooks the web build must not get: the toolbar
// has to clear the home indicator, and iOS's safe-area insets differ from
// Android's. Set on both <html> and <body> so rules can hang off either.
if (dd.isApp()) {
  document.documentElement.classList.add("is-app");
  document.body.classList.add("is-app");
  if (dd.isIos()) {
    document.documentElement.classList.add("is-ios");
    document.body.classList.add("is-ios");
  }
}

/**
 * The basemap the system colour scheme asks for.
 *
 * Dark chrome over the light basemap is the one combination the glass cannot
 * look right in (see the header of src/glass.css), and it is exactly where a
 * dark-mode browser used to land, because the scheme and the basemap were
 * independent settings with independent defaults.
 */
function systemBaseLayer() {
  return get(colorSchemeDark) ? "dark" : "light";
}

/** The basemap a `mapBaseLayer` setting means; see lib/baseLayers.ts. */
function resolveBaseLayer(value: SettingValue): string {
  return resolveSettingBaseLayer(value, get(colorSchemeDark));
}

/**
 * The merged composite used to be a switch in Settings, `layerEuropeComposite`;
 * it is one of the products the tray's picker offers now. A reader who had it
 * on keeps it, as the picker's choice, unless they have made one there since.
 */
function migrateEuropeComposite() {
  try {
    if (localStorage.getItem("layerEuropeComposite") === "true" && localStorage.getItem("radarProduct") === null) {
      localStorage.setItem("radarProduct", "merged");
    }
    localStorage.removeItem("layerEuropeComposite");
  } catch {
    // Storage blocked: nothing was kept to carry over.
  }
}

initUIConstants();   // reads prefers-color-scheme into colorSchemeDark

/* Set before Settings is constructed: its constructor only fires a callback
   when the effective value DIFFERS from the declared default, so with nothing
   stored the store would otherwise stay on the initial value it was declared
   with in stores.ts. */
mapBaseLayer.set(systemBaseLayer());
migrateEuropeComposite();

window.settings = new Settings({
  experimentalFeatures: {
    type: "boolean",
    default: false,
    cb: () => {
      // reportToast(`Experimental features ${value}`);
    },
  },
  mapRotation: {
    type: "boolean",
    default: false,
  },
  /* The glass over the map goes solid while the map moves: smoother on a slow
     phone, a visible flicker everywhere else. See layers/ui.ts, and
     lib/slowDevice.ts for when it turns itself on. */
  solidGlassWhileMoving: {
    type: "boolean",
    default: false,
    cb: (value) => setSolidGlassWhileMoving(Boolean(value)),
  },
  /**
   * Standing notices the reader has closed, as a comma-separated list of
   * message hashes. Written by lib/Toast.ts, which explains the keying; no
   * `cb`, because nothing reacts to it; it is read when a notice is raised.
   */
  dismissedNotices: {
    type: "string",
    default: "",
  },
  precacheForecast: {
    type: "boolean",
    default: true,
    cb: (val) => {
      precacheForecast.set(Boolean(val));
    },
  },
  mapBaseLayer: {
    type: "string",
    default: "system",
    cb: (val) => {
      mapBaseLayer.set(resolveBaseLayer(val));
    },
  },
  radarColorMapping: {
    type: "string",
    default: "classic",
    cb: (value) => {
      radarColorScheme.set(String(value));
      radarColormap.set(String(value));
    },
  },
  capability: {
    type: "string",
    default: "radar",
  },
  layerMesocyclones: {
    type: "boolean",
    default: true,
    cb: (value) => {
      cycloneLayerVisible.set(Boolean(value));
    },
  },
  layerCells: {
    type: "boolean",
    default: true,
    cb: (value) => {
      cellLayerVisible.set(Boolean(value));
    },
  },
  layer3dCells: {
    type: "boolean",
    default: false,
    cb: (value) => {
      cells3dVisible.set(Boolean(value));
    },
  },
  layer3dTerrain: {
    type: "boolean",
    default: true,
    cb: (value) => {
      terrain3dVisible.set(Boolean(value));
    },
  },
  playbackFromStart: {
    type: "boolean",
    default: false,
    cb: (value) => {
      playbackFromStart.set(Boolean(value));
    },
  },
  /* meteocool's own nowcast outside Germany on the forecast steps, beside WN
     (lib/nowcast.ts). Experimental, but on unless turned off: Germany stays
     WN's either way. Nothing about the alerts follows it. */
  meteocoolNowcast: {
    type: "boolean",
    default: true,
    cb: (value) => {
      meteocoolNowcast.set(Boolean(value));
    },
  },
  layer3dFullResolution: {
    type: "boolean",
    default: false,
    cb: (value) => {
      fullResolution3d.set(Boolean(value));
    },
  },
  radarLegendUnit: {
    type: "string",
    default: "pictogram",
    cb: (value) => {
      unit.set(value === "dbz" ? "dbz" : "pictogram");
    },
  },
  radarProduct: {
    type: "string",
    default: DEFAULT_PRODUCT,
    cb: (value) => {
      observedProduct.set(parseObservedProduct(value));
    },
  },
  layerSnow: {
    type: "boolean",
    default: true,
    cb: (value) => {
      snowLayerVisible.set(Boolean(value));
    },
  },
  latLonZ: {
    type: "string",
    default: "49.0,11.0,6",
    source: "url",
  },
  // The wrappers draw their own chrome, so the logo and the layer-switcher
  // button are off there. Enforced in the callback as well as the default,
  // because both are URL-sourced and ?logo=full would otherwise put the web
  // logo back inside the app.
  logo: {
    type: "string",
    default: dd.isApp() ? "none" : "full",
    source: "url",
    cb: (value) => {
      logoStyle.set(dd.isApp() ? "none" : String(value));
    },
  },
  layerswitcher: {
    type: "string",
    default: dd.isApp() ? "no" : "yes",
    source: "url",
    cb: (value) => {
      if (dd.isApp()) {
        layerswitcherVisible.set("no");
        return;
      }
      layerswitcherVisible.set(value === "no" ? "no" : "yes");
    },
  },
  toolbar: {
    type: "string",
    default: "yes",
    source: "url",
    cb: (value) => {
      toolbarVisible.set(value === "no" ? "no" : "yes");
      if (value !== "yes") {
        bottomToolbarMode.set("hidden");
        document.documentElement.style.setProperty("--attributions-bottom-padding", "0px");
      }
    },
  },
  layerLightning: {
    type: "boolean",
    default: true,
    cb: (value) => {
      lightningLayerVisible.set(Boolean(value));
    },
  },
  /* The radars' own marks on the flat map, the way into site mode
     (lib/SiteSweepManager.ts). On unless turned off in Settings. */
  layerSites: {
    type: "boolean",
    default: true,
    cb: (value) => {
      siteMarksVisible.set(Boolean(value));
      if (!value) siteMode.set(null);
    },
  },
  /* The storm motion set by hand on site mode's dial, as "heading,km/h", for
     storm-relative velocity with no storm selected or when the reader
     prefers their own (lib/srvMotion.ts). */
  srvMotion: {
    type: "string",
    default: formatManualMotion(DEFAULT_MANUAL_MOTION),
    cb: (value) => {
      const motion = parseManualMotion(value);
      if (!sameManualMotion(get(srvManualMotion), motion)) srvManualMotion.set(motion);
    },
  },
  // A picture of the map for a headless renderer; see lib/screenshot.ts. Last,
  // so it has the final word over logo, layerswitcher and toolbar above.
  screenshot: {
    type: "string",
    default: "no",
    source: "url",
    cb: (value) => {
      if (!isScreenshot(value)) return;
      document.documentElement.classList.add(SCREENSHOT_CLASS);
      logoStyle.set("none");
      layerswitcherVisible.set("no");
      toolbarVisible.set("no");
      bottomToolbarMode.set("hidden");
    },
  },
});
/* A link says which overlays the sender had on. Held for this page load only,
   and before the stores below first read their settings, so the layers come up
   the way the link says without the link rewriting the reader's own choices. */
applyLinkedOverlays(window.settings);

/* A screenshot draws the radar, the strikes and the cyclones once, and keeps
   nothing up to date: no socket, no timers, no cells or "3D" tags. */
const screenshot = isScreenshot(window.settings.get("screenshot"));
// A picture has no frame rate to judge.
if (!screenshot) watchForSlowDevice(window.settings);
// Held like a link's overlays, so the reader's own setting is not touched.
// The tracks are only asked for once the map has drawn, so they would land
// on the picture after it was declared finished.
if (screenshot) window.settings.override("layerCells", false);
// The basemap and palette a picture was asked for, held the same way. Before
// the maps are built, so they come up in that look; see lib/screenshot.ts.
if (screenshot) applyScreenshotLook(window.settings, window.location.search);

const [lightningSource, lightningLayer] = makeLightningLayer();
lightningLayerVisible.subscribe((value) => {
  lightningLayer.setVisible(value);
  window.settings.set("layerLightning", value);
});
lightningLayerVisible.set(window.settings.getBoolean("layerLightning"));

const nb = progress();
/** How recent a wake makes a socket reconnect's own resync redundant. */
const SOCKET_RESYNC_QUIET_MS = 15_000;
// WebSocket first: socket.io's default opens a long-polling transport and
// upgrades it, which is three requests and a sticky-session hazard before the
// first poke. Polling is the fallback for a network that blocks it, but only
// with `tryAllTransports`: without it engine.io opens the first transport
// on every attempt and a network that blocks WebSockets never gets a poke.
const radarSocketIO: Socket<ServerToClientEvents, ClientToServerEvents> = io(`${websocketBaseUrl}/radar`, {
  transports: ["websocket", "polling"],
  tryAllTransports: true,
  autoConnect: !screenshot,
});
/* A socket that comes back has missed whatever was poked while it was gone,
   and nothing else says so: the map would keep the frames it had until the
   next poke, minutes away. So a reconnect resyncs, unless a wake has just
   done that (coming back to the page reconnects the socket too). */
let socketConnectedBefore = false;
radarSocketIO.on("connect", () => {
  console.log("radar/forecast websocket connected!");
  if (socketConnectedBefore && !wokeWithin(SOCKET_RESYNC_QUIET_MS)) wake("socket reconnected");
  socketConnectedBefore = true;
});

const strikemgr = new StrikeManager(1000, lightningSource);

const [mesocycloneSource, mesocycloneLayer] = makeMesocycloneLayer();
const mesocyclonemgr = new MesoCycloneManager(100, mesocycloneSource);
cycloneLayerVisible.subscribe((value) => {
  mesocycloneLayer.setVisible(value);
  window.settings.set("layerMesocyclones", value);
});
// This restored the *lightning* store from the mesocyclone setting, so the
// mesocyclone layer never came back and the lightning visibility read three
// lines above was immediately overwritten.
cycloneLayerVisible.set(window.settings.getBoolean("layerMesocyclones"));

const [pulseSource, pulseLayer, stopPulse, crowded] = makeCellPulseLayer();
const [cellSource, cellLayer] = makeCellLayer(crowded);
const stormmgr = new StormManager(cellSource, pulseSource);
onDestroy(stopPulse);
/* From a seed to its storm, and the storm to its detail; see lib/stormResolver.ts. */
const stormResolver = startStormResolver(nb);
onDestroy(stormResolver.stop);
cellLayerVisible.subscribe((value) => {
  stormmgr.enable(Boolean(value));
  window.settings.set("layerCells", value);
  if (value) stormmgr.reload(get(mapExtent4326), { force: true, nanobar: nb });
});
cellLayerVisible.set(window.settings.getBoolean("layerCells"));

/**
 * Cells are drawn on every observed frame, the newest and the earlier ones,
 * and never on the nowcast, which has no detections to show.
 *
 * Marks left at the present while the radar under them moves sit beside
 * echoes they have nothing to do with, and read as a tracker that has lost
 * its storms. On an earlier frame the manager rewinds every track to that
 * moment out of the answer it already holds (see lib/stormMarks.ts): no request,
 * and cheap enough to follow a playback or a scrub frame by frame.
 *
 * The test is the frame on screen against the newest one the grid holds, not
 * the `live` store the pill uses: that is also cleared when the radar is
 * stale or a grid request fails, even though the frame has not changed.
 * The test is also one-way on purpose:
 * both stores at 0 means no grid has arrived, which is a page that has not
 * loaded its radar or is not showing radar at all, and there is no frame there
 * for the cells to disagree with. Hiding is for the case we can positively
 * see, where the player is parked somewhere the storms are not.
 *
 * The manager keeps running throughout: this hides the drawing and keeps the
 * data, so coming back to the live edge costs no refetch.
 */
/** The frame the cells were last drawn for, so a move to another one is seen. */
let cellsAt: "hidden" | "now" | number = "hidden";
derived(
  [cellLayerVisible, capTimeIndicator, capLatestObservation],
  ([wanted, shown, newest]): "hidden" | "now" | number => {
    if (!wanted) return "hidden";
    if (showsLatestFrame(shown, newest)) return "now";
    return shown < newest ? shown : "hidden";
  },
).subscribe((at) => {
  const value = at !== "hidden";
  if (value) stormmgr.showAt(at === "now" ? null : at * 1000);
  cellLayer.setVisible(value);
  // The ping only ever marks cells the tracks layer is already drawing, so it
  // appears and disappears with it rather than carrying a rule of its own.
  pulseLayer.setVisible(value);
  // The panel stays open on another frame its storm is drawn on: the newest,
  // or an earlier one it had already appeared by. Before that, or with the
  // marks hidden, it would be a panel about a storm the map is not showing.
  // A grid refresh on the live frame is not another frame.
  const current = get(selectedStorm);
  if (at !== cellsAt && current && !(at === "now" || (at !== "hidden" && existedBy(current, at * 1000)))) {
    selectedStorm.set(null);
  }
  cellsAt = at;
});

/* Trial: a "3D" tag beside every storm core the 3D map can cut open, and the
   way there (see layers/cloudHints.ts). Only where that map is offered, and,
   like the cells, only on the newest observation, which is the scan the cores
   were found in. A core older than that radar is tagged grey. */
const hintsWanted = capabilityEnabled("cells3d") && !screenshot;
const hints = makeCloudHints();
const cloudHintLayer = hints.layer;
derived(
  [capTimeIndicator, capLatestObservation],
  ([shown, newest]) => hintsWanted && showsLatestFrame(shown, newest),
).subscribe((value) => cloudHintLayer.setVisible(value));

const cloudHints = new VolumeFeed<CurrentVolumes>(
  () => fetchCurrentVolumes(quietly).catch(() => null),
  (answer) => {
    const volumes = (answer.volumes ?? []) as RadarVolume[];
    hints.setClouds(volumes);
    // And for a storm opened on this map, the box its patch is in.
    offerVolumes(volumes);
  },
);

/* Site mode: a mark on every radar with sweeps lately, and tapped, that
   radar's own sweep drawn in its polar bins over the dimmed composites, with
   rings of beam height (lib/SiteSweepManager.ts). Not
   in a screenshot, which is of the composites. */
const siteMarks = makeRadarSiteMarks();
const siteSweep = makeSiteSweepLayer();
const beamRings = makeBeamRings();
const siteCredit = (network: string) => (network === "de"
  ? dwdAttribution
  : NETWORKS.find((candidate) => candidate.code === network)?.attribution ?? null);
const siteSweeps = new SiteSweepManager(siteMarks, siteSweep, beamRings, siteCredit);
readThrough(siteSweeps);
siteMarksVisible.set(!screenshot && window.settings.getBoolean("layerSites"));
siteMarksVisible.subscribe((value) => {
  if (!screenshot) window.settings.set("layerSites", value);
});
/* Kept once the dial comes to rest: a drag turns it through dozens of
   headings, and each would be a write. */
srvManualMotion.set(parseManualMotion(window.settings.get("srvMotion")));
let srvMotionWrite: ReturnType<typeof setTimeout> | undefined;
srvManualMotion.subscribe((motion) => {
  clearTimeout(srvMotionWrite);
  srvMotionWrite = setTimeout(() => window.settings.set("srvMotion", formatManualMotion(motion)), 400);
});

/* The marks for the radars in view, once the map settles where they are
   drawn at all. */
derived([mapExtent4326, zoomlevel, siteMarksVisible], (values) => values).subscribe(([extent, zoom, shown]) => {
  // Past the marks' least zoom, which OpenLayers takes as exclusive.
  if (extent && shown && zoom > SITE_MARKS_MIN_ZOOM) void siteSweeps.listViewport(extent);
});

/* `run` is the scan of a KONRAD3D run that has just landed. Its cores are
   usually not built yet, and fetched only now the tags stood a scan behind
   until the next run; they are waited for (see lib/scans.ts). Without one, a
   wait already under way goes on: a network's run landing says nothing of
   whether DWD's cores are built yet. */
async function reloadCloudHints(run?: number, bar: Progress = nb) {
  if (!hintsWanted) return;
  const answer = await fetchCurrentVolumes(bar).catch(() => null);
  if (answer) cloudHints.offer(answer);
  if (run !== undefined) cloudHints.follow(run);
}

/* The panel is a property of a selection and cannot outlive one. Anything that
   drops the selection (the map background, the layer switcher, the scrubber
   leaving the live edge) therefore closes it without having to remember to. */
selectedStorm.subscribe((storm) => {
  if (!storm) stormDetails.set(false);
});

/* So is the slice. Every storm opens cut along its own track: an angle turned
   for the last one means nothing for this one, whose track points somewhere
   else. Reset on the selection instead of on the cutaway mounting, where it
   used to be, so walking the family re-cuts each cell it lands on, and a link
   restoring a storm cut at some angle can set the angle after the storm
   without the panel's mount undoing it. Keyed on the storm, not on the
   object: a refresh hands the same storm over as a fresh copy every few
   minutes, and that is not a new storm. Nor is finding the storm a tapped
   cell or box belongs to (`isResolution`). */
{
  let cutFor: import("./lib/stormSelection").StormSelection | null = null;
  selectedStorm.subscribe((storm) => {
    const same = selectionKey(storm) === selectionKey(cutFor) || isResolution(cutFor, storm);
    cutFor = storm;
    if (!same) cutRotationDeg.set(0);
  });
}

/**
 * The drawer holds one thing: tapping a storm while the model comparison is
 * open replaces it, as tapping a second storm replaces the first.
 */
derived(selectedStorm, (storm) => Boolean(storm))
  .subscribe((storm) => { if (storm) modelCompareAt.set(null); });

/**
 * And playback puts it away: the frames it steps through are the map, which
 * the comparison's drawer covered.
 */
playbackRunning.subscribe((running) => { if (running) modelCompareAt.set(null); });

/**
 * Closing the player, or its return to the live frame, lets the frames it
 * stepped through go; see `forgetEarlierFrames`.
 */
let playerOpen = false;
bottomToolbarMode.subscribe((mode) => {
  if (playerOpen && mode !== "player") forgetEarlierFrames();
  playerOpen = mode === "player";
});

const closeCompare = () => modelCompareAt.set(null);
/** A box of sky's sheet closes the selection: it leaves no marks on the flat map behind it. */
const closeStorm = () => selectedStorm.set(null);
/** Whether the selected storm stands cut open on the 3D map behind its sheet. */
$: cutOpen = $sharedActiveCap === "cells3d";
/** A storm with no KONRAD3D history to scroll through rests in a short sheet there. */
$: compactSheet = Boolean($selectedStorm && !$selectedStorm.cell);
/**
 * The phone's sheet closing: back to the marks for a storm on the flat map
 * (lib/cellSelection.ts `afterClose`), the whole selection for a box of sky
 * with no storm found, which has no marks to go back to.
 */
function closeSheet() {
  const storm = get(selectedStorm);
  if (storm && !storm.id && !storm.cell) closeStorm();
  else stormDetails.set(false);
}

// Storms are fetched for what is on screen, so they follow the map rather
// than a timer. The manager ignores a move that stays inside what it already
// holds, which is most of them.
mapExtent4326.subscribe((extent) => {
  stormmgr.reload(extent, { nanobar: nb });
});

// The event is a nudge, not the storms: only the ones in view are worth
// asking for, and only once someone is looking. A hidden tab takes the newest
// on its way back. The open storm's detail is asked for again with them.
radarSocketIO.on("storms", () => {
  whenVisible("storms", () => {
    stormmgr.reload(get(mapExtent4326), { force: true, nanobar: nb });
    stormResolver.refresh();
  });
});

// Gathered for a moment rather than drawn one by one: each strike on its own
// reclustered all of them and redrew the whole map, many times a second in a
// thunderstorm. See lib/coalesce.ts.
const liveStrikes = coalesce<{ lon: number; lat: number; time: number }>(
  (strikes) => strikemgr.addLiveStrikes(strikes),
  LIVE_STRIKE_BATCH_MS,
);
radarSocketIO.on("lightning", (data) => liveStrikes.push(data));
onDestroy(() => liveStrikes.cancel());
window.ll = lightningLayer;
// A payload that is not a list (an error body, a half-delivered response) is
// skipped, keeping the cyclones already shown, rather than thrown on.
radarSocketIO.on("mesocyclones", (data) => {
  if (!Array.isArray(data)) return;
  mesocyclonemgr.clearAll();
  data.forEach((elem) => mesocyclonemgr.addCyclone(elem));
});

// Both managers cap themselves by count, but nothing ever dropped features for
// being old: fadeStrikes/fadeCyclones existed and were never called, so a quiet
// day left half-hour-old strikes on the map until the ring buffer wrapped.
const FADE_INTERVAL_MS = 5 * 60 * 1000;
const fadeInterval = screenshot ? undefined : window.setInterval(() => {
  strikemgr.fadeStrikes();
  mesocyclonemgr.fadeCyclones();
}, FADE_INTERVAL_MS);
onDestroy(() => window.clearInterval(fadeInterval));

// Was `export let lm`, assigned from inside the component: an outward binding
// for a named import that nothing used, and a prop you cannot write to in
// Svelte 5. It is reachable as window.lm, which is what the apps call.
const lm = new LayerManager({
  settings: window.settings,
  nanobar: nb,
  initialCapability: openingLink.layer,
  capabilities: [
    {
      name: "radar",
      capability: RadarCapability,
      additionalLayers: [
        pulseLayer, cellLayer, cloudHintLayer, mesocycloneLayer, lightningLayer, labelsOnly(),
        radolanOverlay({ followsForecast: true }),
        siteSweep.layer, beamRings.layer, siteMarks.layer,
      ],
      options: {
        nanobar: nb,
        socket_io: radarSocketIO,
        screenshot,
      },
    },
    {
      name: "lightning",
      capability: LightningCapability,
      additionalLayers: [labelsOnly()],
      options: {
        nanobar: nb,
        hasBaseLayer: true,
        socket: radarSocketIO,
      },
    },
    {
      name: "cells3d",
      capability: Cells3DCapability,
      // No OpenLayers layers: MapLibre draws this one. The bare ol/Map it still
      // receives is the camera the layer switcher previews and the shared View
      // rides on.
      options: { nanobar: nb, hasBaseLayer: true },
    },
    {
      name: "precipTypes",
      capability: PrecipitationTypesCapability,
      additionalLayers: [labelsOnly(), radolanOverlay()],
      options: {
        nanobar: nb,
      },
    }].filter((descriptor) => capabilityEnabled(descriptor.name))
    // A screenshot is of the radar map. Without the 3D one, MapLibre and the
    // storm volumes are never loaded.
    .filter((descriptor) => !screenshot || descriptor.name === "radar"),
});
window.lm = lm;

/* The 3D map drapes the same radar frame the flat map is showing, so the two
   never disagree about what the weather is. RadarCapability already resolves
   which frame is current and what its tiles are; this forwards it, with its
   scan, instead of working it out a second time. The 3D map waits for the
   storms' volumes to reach that scan; see lib/scans.ts. */
const cells3d = lm.getCapability("cells3d") as Cells3DCapability | undefined;
const radarCap = lm.getCapability("radar") as RadarCapability | undefined;
if (cells3d && radarCap) {
  // HX and the networks, whichever product the flat map draws: the storms on
  // the 3D map are the column already, and are judged against these scans.
  const forwardRadarFrame = () => {
    const step = radarCap.getMostRecentObservation();
    const frame = radarCap.clientGrid?.[step];
    cells3d.setRadarFrame(frame?.url ?? null, step, frame?.tiles);
  };
  // The networks' own newest frames go the same way: the 3D map drapes them
  // over their countries, cut out of DWD's frame as the flat map does.
  const forwardNetworkFrames = () => {
    cells3d.setNetworkFrames(radarCap.liveNetworkFrames());
  };
  radarCap.addObserver((subject) => {
    if (subject === "grid") forwardRadarFrame();
    if (subject === "networks") {
      forwardRadarFrame();
      forwardNetworkFrames();
    }
  });
  forwardNetworkFrames();
  // A new run means new cells as well as a new frame. Not in a hidden tab,
  // where it was the cells and megabytes of volumes for nobody; the newest
  // run is fetched on the way back.
  radarSocketIO.on("cells", () => whenVisible("cells3d", () => cells3d.newRun()));
  // And every network's runs, which have no KONRAD3D run to follow, as they
  // are built; in a hidden tab, once, on the way back.
  radarSocketIO.on("volumes", () => whenVisible("cells3dVolumes", () => cells3d.newVolumes()));
  // The same strikes the flat map is drawing, read out of its ring buffer
  // rather than collected a second time off the socket.
  cells3d.setStrikeSource(lightningSource);
  // And the frames of the last two hours, for an earlier scan the reader
  // picks to have its own radar under it, as the flat map would show it.
  cells3d.setRadarHistory((step) => radarCap.observedAt(step));
}

/* The tags are judged against the same radar: a frame landing ahead of its
   scan's cores takes their tags off, and the cores landing (the nudges
   below) put them back. */
if (radarCap && hintsWanted) {
  const forwardHintRadar = () => hints.setRadar(radarCap.liveRadarScans());
  radarCap.addObserver((subject) => {
    if (subject === "grid" || subject === "networks") forwardHintRadar();
  });
  forwardHintRadar();
}

// The cores are rebuilt with each run, so the tags follow the same nudge.
// Without the loading bar: a refresh of something already on screen.
radarSocketIO.on("cells", (cells) => {
  whenVisible("cloudHints", () => void reloadCloudHints(Math.floor(cells.reference_time / 1000), quietly));
});
// A radar published new sweeps: the open one's are refetched, in a visible tab.
radarSocketIO.on("sweeps", (event) => {
  whenVisible("siteSweeps", () => void siteSweeps.sitePublished(event.site));
});

// And with each network's runs as they are built, which no KONRAD3D run announces.
radarSocketIO.on("volumes", () => {
  whenVisible("cloudHints", () => void reloadCloudHints(undefined, quietly));
});
void reloadCloudHints();

// Thin scrollbars that show only while something scrolls; see lib/scrollbars.ts.
installScrollbars();

// The panels' "open in 3D" links and the tags above all switch through this,
// and the way back (see below) through the same manager.
if (cells3d) registerOpen3D(() => lm.setTarget("cells3d", "map"), (cap) => lm.setTarget(cap, "map"));

/*
 * A phone has room for the 3D map or the flat map's playback, not both.
 *
 * The 3D map is kept when the reader leaves it, so coming back is instant,
 * and playback keeps every frame it steps through. Together they passed
 * iOS's 2 GB for a web view on an iPhone 16 Pro, which killed it (October
 * 2026). So stepping through frames, by Play or by a hand on the strip, lets
 * the 3D map go, to be built again when it is next opened (the player itself
 * is always open, so opening it says nothing), and opening the 3D map lets
 * playback's frames go at once:
 * closing the player only lets them go on the flat map's next drawn frame,
 * which does not come while the 3D map is on screen.
 */
if (cells3d && isHandheld()) {
  browsingFrames.subscribe((browsing) => { if (browsing) cells3d.unload(); });
}
sharedActiveCap.subscribe((cap) => { if (cap === "cells3d") dropEarlierFrames(); });

/*
 * A storm opened on the 3D map from the flat one goes back when it closes.
 *
 * Opening a tag or a panel's "open on the 3D map" is a detour to look at one
 * storm; closing that storm is the end of it, and the reader is put back on
 * the map they left rather than on a tilted map they did not pick, one
 * switcher away from the radar. The camera settles to nadir and north-up over
 * the view they left first, so the flat map appears where the 3D one landed
 * and nothing jumps. Closing means the popup or sheet going: on a phone the
 * sheet can close with the cell still selected, and that counts.
 *
 * Only for a detour. A 3D map picked from the switcher or opened by a link
 * has nothing to go back to, and lib/open3d.ts forgets the origin as soon as
 * the reader is on any other map by their own doing.
 */
let leaving3D = false;
derived(
  [selectedStorm, stormDetails, sharedActiveCap],
  ([storm, details, cap]) => ({ open: Boolean(storm && details), cap }),
).subscribe(({ open, cap }) => {
  if (cap !== "cells3d") {
    forget3DOrigin();
    return;
  }
  const origin = origin3D();
  if (open || !origin || leaving3D || !cells3d) return;
  leaving3D = true;
  cells3d.leave(origin.view).finally(() => {
    leaving3D = false;
    // Something was opened while the camera was on its way: the reader is
    // looking at it, and stays.
    if (get(selectedStorm) && get(stormDetails)) return;
    returnFrom3D();
  });
});

/**
 * Whether this is a device with a real pointer that can hover.
 *
 * Asked once. A touch screen reports neither, and a hover cursor there is a
 * style nobody can see bought with a hit test on every frame of every drag.
 */
const finePointer = typeof window === "undefined" || !window.matchMedia
  ? null
  : window.matchMedia("(hover: hover) and (pointer: fine)");

/*
 * Tapping a storm opens its history.
 *
 * The long press is already the radar's "what is falling here" gesture, and it
 * asks about a point rather than about an object, so cells take the plain tap
 * instead. `forEachFeatureAtPixel` stops at the first cell feature under the
 * finger, which may be the path or the forecast dots as easily as the centroid;
 * they all carry their track's code.
 *
 * A cell before a "3D" tag, when the finger is on both: the tag sits beside
 * its core, which is usually inside a cell, so it caught taps aimed at the
 * cell and switched maps instead of opening the storm. The 3D map ranks the
 * two the same way. The tag answers where no cell is under the finger.
 */
/*
 * The first hit test on a page costs 150 ms whatever it asks: OpenLayers
 * times canvas readback three ways, 50 ms each, to choose how to read its
 * hit-detection canvas, and only then answers. That was the first hover over
 * the map, or the first tap on a storm, freezing the map. Asking any question
 * once the map is drawn and the page is idle pays it while nobody is waiting.
 * The benchmark is OpenLayers' own and runs once for the page, and any drawn
 * vector layer gets it there.
 */
function whenIdle(task: () => void) {
  // Missing from older Safari, whatever the DOM types say.
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(task, { timeout: 5000 });
  else window.setTimeout(task, 2000);
}

lm.forEachMap((map) => {
  /* `mapExtent4326` is published on moveend, so on a cold load (where the
     view comes from the URL before the map has a target) it stays null until
     the user pans, and the layer would sit empty behind a map full of storms.
     The first completed render is when there is a viewport to ask about. */
  map.once("rendercomplete", () => {
    if (!screenshot) whenIdle(() => map.hasFeatureAtPixel([0, 0]));
    const size = map.getSize();
    if (!size) return;
    stormmgr.reload(transformExtent(
      map.getView().calculateExtent(size),
      "EPSG:3857",
      "EPSG:4326",
    ) as [number, number, number, number], { nanobar: nb });
  });

  map.on("singleclick", (event) => {
    /* A radar's mark opens it in site mode, before anything under it: the
       mark is a fixed place, and a storm passing over it is tapped beside it. */
    if (siteMarks.layer.getVisible()) {
      const radar = map.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature.get("radarSite") as string | undefined,
        { layerFilter: (layer) => layer === siteMarks.layer, hitTolerance: 4 },
      );
      if (radar) {
        siteSweeps.open(radar);
        return;
      }
    }
    const cellsShown = get(cellLayerVisible);
    const code = cellsShown
      ? map.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature.get("storm") as string | undefined,
        { layerFilter: (layer) => layer === cellLayer, hitTolerance: 6 },
      )
      : undefined;
    if (!code && cloudHintLayer.getVisible()) {
      const cloud = map.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature.get("cloud") as RadarVolume | undefined,
        { layerFilter: (layer) => layer === cloudHintLayer, hitTolerance: 4 },
      );
      if (cloud) {
        openCloudIn3D(cloud);
        return;
      }
    }
    /* With a radar open, a click past its reach that hits no storm or cloud
       closes it, and only it: the storm a storm-relative sweep is read
       against stays selected. */
    if (!code && get(siteMode)) {
      const [lon, lat] = toLonLat(event.coordinate);
      if (!siteSweeps.reaches(lon, lat)) {
        siteSweeps.close();
        return;
      }
    }
    if (!cellsShown) return;
    const current = get(selectedStorm);
    const next = nextSelection(
      { code: current?.id ?? (current?.cell ? `cell:${current.cell.code}` : null), details: get(stormDetails) },
      code ?? null,
      get(smallScreen),
    );
    const tapped = next.code ? stormmgr.stormFor(next.code) : undefined;
    // A storm the map holds, kept with what is known of it if it is the open
    // one; a mark of the open selection the map does not hold (a cell's
    // forecast before its storm is found) leaves it as it is.
    if (tapped) selectedStorm.set(withStorm(current, tapped));
    else if (!next.code) selectedStorm.set(null);
    stormDetails.set(next.details);
  });

  /*
   * The cursor says what is clickable, which on a map is otherwise invisible.
   *
   * A storm centroid is a 6px dot among a screenful of radar; nothing about it
   * says it answers a click, and on a desktop the pointer is the one channel
   * that can say so without drawing anything.
   *
   * It runs the same hit test as the tap above, hit tolerance included, so the
   * pointer can never promise a cell that a click would then miss. Written on
   * the map's own viewport rather than on the shared `#map` target, so a
   * capability switch cannot leave another one wearing this one's cursor.
   */
  let hovering = false;
  const viewport = map.getViewport();
  map.on("pointermove", (event) => {
    // Nothing to say mid-drag: the cursor belongs to the pan for its duration,
    // and hit-testing every frame of one buys a style nobody is looking at.
    if (!finePointer?.matches || event.dragging) return;
    const over = map.hasFeatureAtPixel(event.pixel, {
      layerFilter: (layer) => (layer === cellLayer && get(cellLayerVisible))
        || (layer === cloudHintLayer && layer.getVisible()),
      hitTolerance: 6,
    });
    if (over === hovering) return;
    hovering = over;
    viewport.style.cursor = over ? "pointer" : "";
  });
  /* A pointer that leaves over a cell never reports leaving it. */
  viewport.addEventListener("pointerleave", () => {
    if (!hovering) return;
    hovering = false;
    viewport.style.cursor = "";
  });
});

window.settings.setCb("mapRotation", (value) => {
  // Settings callbacks can fire before a capability is on screen, and there is
  // no view to carry over then.
  const current = lm.getCurrentMap();
  if (!current) return;
  const newView = new View({
    center: current.getView().getCenter(),
    zoom: current.getView().getZoom(),
    minZoom: current.getView().getMinZoom(),
    enableRotation: Boolean(value),
    extent: WEB_MERCATOR_EXTENT,
    // The tray's strip, which the centre above is measured inside. Left off,
    // the map jumped by half a tray and stayed unpadded until the bars next
    // moved.
    padding: current.getView().padding,
  });
  lm.forEachMap((map) => map.setView(newView));
});
window.settings.setCb("latLonZ", (value) => {
  if (!value) return;
  const parts = String(value).split(",");
  if (parts.length !== 3) return;
  const [lat, lon, z] = parts.map(parseFloat);
  const view = lm.getCurrentMap()?.getView();
  if (!view) return;
  // The middle of the map element, which is what a URL's latLonZ records.
  view.setZoom(z);
  setElementCentre(view, fromLonLat([lon, lat]));
});

// Both of these used to finish a nanobar task keyed on the global `URL`
// constructor rather than on a URL string, and neither had a matching start.
async function reloadLightning() {
  const strikes = await fetchLightningCache(nb).catch(() => null);
  if (!strikes) return;
  strikemgr.clearAll();
  strikemgr.addStrikes(strikes.map((strike) => ({ lon: strike.lon, lat: strike.lat, time: Math.round(strike.time) })));
}

async function reloadCyclones() {
  const detections = await fetchMesocyclones(nb).catch(() => null);
  if (!Array.isArray(detections)) return;
  mesocyclonemgr.clearAll();
  detections.forEach((detection) => mesocyclonemgr.addCyclone(detection));
}

const lightningLoaded = reloadLightning();
const cyclonesLoaded = reloadCyclones();

/* No WebGL, no radar: layers/webglTile.ts keeps those layers off the map so
   the rest of it still draws, and this says why the rain is missing. */
if (!screenshot && !webglSupported()) reportNotice($_("notice.no_webgl"));

if (screenshot && radarCap) {
  whenDrawn(
    radarCap.getMap(),
    Promise.allSettled([radarCap.loaded, lightningLoaded, cyclonesLoaded]),
    markScreenshotReady,
  );
}

/* Everything that has to happen when the page starts running again. The web
   never reached this: the hook existed for the two native apps to call, and
   nothing in the browser called it, so a tab that had been asleep sat on its
   expired frames until the next poke happened to arrive. lib/wakeup.ts is the
   browser's side of it. */
const unsubscribeWake = onWake(() => {
  // A picture is taken once; the entrypoint does not watch for wakes either.
  if (screenshot) return;
  lastFocus.set(new Date());
  if (window.matchMedia) {
    colorSchemeDark.set(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark )").matches);
  }
  /* socket.io's reconnect backoff is a timer, and it was suspended along with
     everything else, so the socket can sit disconnected for minutes after we
     are back. The poke that refreshes the map comes over it. */
  if (radarSocketIO.disconnected) radarSocketIO.connect();
  /* The cells follow the map and the socket, and neither moves for a page
     that was away or a fetch that failed: nothing else would ask again. */
  stormmgr.reload(get(mapExtent4326), { force: true, nanobar: nb });
  stormResolver.refresh();
  /* The map on screen catches up on what only switching to it fetches, and
     the tiles lost while the network was down are asked for again: neither
     OpenLayers nor MapLibre retries a tile that failed. */
  lm.resync();
  if (retryFailedTiles()) lm.forEachMap((map) => map.render());
  reloadLightning();
  reloadCyclones();
  void reloadCloudHints();
});
onDestroy(unsubscribeWake);

/* The apps call this directly. Routed through wake() so a native foreground
   and the browser signals that accompany it still only resync once. */
window.enterForeground = () => wake("native bridge");

/* The scheme can flip while the app is open, and the chrome follows it live,
   so the map has to as well or they end up disagreeing. Only while the basemap
   follows the system: writable.set() with an unchanged value notifies nobody,
   so the immediate first call here is a no-op. */
const baseLayerSub = colorSchemeDark.subscribe(() => {
  mapBaseLayer.set(resolveBaseLayer(window.settings.get("mapBaseLayer")));
});
onDestroy(baseLayerSub);

const refreshSub = tileRefreshSignal.subscribe((n) => {
  if (n > 0) lm.refreshTiles();
});
onDestroy(refreshSub);

/* Coming back online does not make OpenLayers re-request the tiles that failed
   while it was down, so nothing recovers on its own. That is what the banner's
   retry button was for; with the banner folded into the Latest pill there is no
   button, so the reconnection does it. */
let wasOnline = true;
const reconnectSub = networkStatus.subscribe(({ online }) => {
  if (online && !wasOnline) tileRefreshSignal.update((n) => n + 1);
  wasOnline = online;
});
onDestroy(reconnectSub);
onDestroy(cleanupUIConstants);

/* The URL from here on: the rest of the opening link, once there is data for
   it, then the address bar kept in step with the page, and Back and Forward. */
onDestroy(startUrlState({
  lm, settings: window.settings, stormmgr, cells3d, nanobar: nb,
}));
onDestroy(exposeShareLink());

if (postInitCb) postInitCb(lm);

</script>

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    overflow: hidden;
    /* The basemap earth colour, so nothing flashes under the tiles. */
    background-color: var(--mc-map-bg);
    color: var(--mc-text);
    font-family: var(--mc-font);
  }

  /* The one loading bar; lib/NanobarWrapper.ts builds it and runs it. Its
     fill used to be the bare class `.bar`, which also styled every bar of
     the chart skeletons (ChartSkeleton.svelte) with this one's glow. */
  :global(.nanobar) {
    position: fixed;
    left: 0;
    width: 100%;
    height: 2px;
    z-index: var(--mc-z-nanobar);
    top: var(--mc-safe-top);
    pointer-events: none;
  }
  :global(.nanobar-fill) {
    width: 0;
    height: 2px;
    background: var(--mc-brand);
    border-radius: 0 2px 2px 0;
    /* No glow: hard against the top edge, a blur can only spread downwards,
       and under the line it read as a second, fainter bar. */
  }

  /* .sl-toast-stack and the sl-alert parts live in src/glass.css. */

  /* The page itself is never zoomable.
     `manipulation` was not enough: it only turns off double-tap-to-zoom and
     still permits pinch, and the platforms treat a double-tap that turns into a
     drag as a pinch, which is how a double tap on the loop button in the
     player ended up zooming the whole page. `pan-x pan-y` allows scrolling and
     nothing else, so the diagnostics panel still scrolls, the map still pans,
     and the only zoom left anywhere is the map's own.
     Not `none`: that would take scrolling with it. Elements that want the whole
     gesture (the strip's swipe, the pill) still set `none` for themselves. */
  /* Anchored rather than floating over the tap: the map animates under a
     popup, and a panel that chases the storm is harder to read than one that
     stays put. Above the toolbar, clear of the bottom tray. */
  /* The drawer material (src/glass.css) supplies the fill, blur, shadow and
     ink; this is the frame. It does not scroll itself: the content scrolls
     inside it, so what runs past the top and bottom can fade out into the
     glass. A mask on the frame would fade the glass along with it. */
  .cell-details-panel {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: var(--mc-z-details);
    display: flex;
    flex-direction: column;
    max-width: min(392px, calc(100vw - 24px));
    max-height: calc(100vh - 24px);
    overflow: hidden;
    border: 1px solid var(--mc-drawer-edge);
    border-radius: var(--mc-radius-tray);
    box-sizing: border-box;
  }
  .cell-details-panel > .scroll {
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px var(--mc-drawer-pad) var(--mc-fade-bottom);
    -webkit-mask-image: linear-gradient(to bottom,
      transparent, #000 var(--mc-fade-top),
      #000 calc(100% - var(--mc-fade-bottom)), transparent);
    mask-image: linear-gradient(to bottom,
      transparent, #000 var(--mc-fade-top),
      #000 calc(100% - var(--mc-fade-bottom)), transparent);
  }

  /* The bottom trays step aside for a sheet on a phone.

     The sheet is most of the screen there, and what is left of the map is the
     strip below it, which is exactly where the scale, the clock and the
     playback controls sit. Hidden through a class on <body>, which CellSheet
     sets while any sheet is up, rather than by not rendering them: the player
     owns subscriptions to the radar grid and its own playback state, and
     tearing that down and rebuilding it every time a sheet opens would be a
     lot of machinery moved for a visual answer. Both trays carry
     .bottomToolbar. */
  :global(body.cell-details-open .bottomToolbar) {
    display: none;
  }

  /* MapLibre is appended into the map element rather than replacing it, so it
     has to be told to fill it; OpenLayers sizes its own viewport. */
  :global(.maplibre-host) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  :global(*) {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    touch-action: pan-x pan-y;
  }
</style>

{#if !dd.isApp()}
  <Logo layerManager={lm} />
{/if}

<!-- toolbarVisible holds "yes"/"no", and "no" is a truthy string: testing the
     store itself left ?toolbar=no showing the whole toolbar. -->
{#if $toolbarVisible === "yes"}
  <BottomToolbar layerManager={lm} />
{/if}

<Map layerManager={lm} />
<!-- The glass veil over the map while the 3D map is brought up for the
     first time; gated on the active map as well, so switching away during
     the bring-up takes it with it. -->
{#if ($cells3dLoading || $cells3dFailed) && $sharedActiveCap === "cells3d"}
  <MapLoading failed={$cells3dFailed} onretry={() => cells3d?.retry()} />
{/if}
<!-- The 3D map's legend, peel and scan picker, once it is up. In the apps as
     well: their own chrome is the zoom, locate and compass column, and none
     of this is in it. A toolbar asked away is a display that wants none of
     it either. -->
{#if $sharedActiveCap === "cells3d" && !$cells3dLoading && !$cells3dFailed && $toolbarVisible === "yes"}
  <!-- Not while a storm is open: a cut storm does not peel, and its panel
       says when it was seen. With the picker of which scan the storms are
       from, which stays on its choice meanwhile. -->
  {#if !$selectedStorm}
    <PeelSlider pickScan={cells3d ? (scan) => cells3d.showScan(scan) : null} />
  {/if}
  <Guide3D />
{/if}
<PointMenu layerManager={lm} />

{#if $radarExplainerOpen}
  <Lazy load={loadRadarExplainer} floating let:module>
    <svelte:component this={module.default} on:close={() => radarExplainerOpen.set(false)} />
  </Lazy>
{/if}


{#if $selectedStorm && $stormDetails}
  {#if $smallScreen}
    <!-- One glass sheet for whatever storm was tapped. On the 3D map, a storm
         KONRAD3D does not track is cut open on the map itself, so its sheet
         is only as tall as what it holds (a few facts and the dial that turns
         the cut) until it is pulled up for how the volume was built. -->
    <Lazy load={loadStormSheet} floating let:module>
      <svelte:component this={module[0].default} fitAtRest={cutOpen && compactSheet} onClose={closeSheet}
        let:expanded let:expand>
        <svelte:component this={module[1].default} selection={$selectedStorm}
          compact={cutOpen && compactSheet} {expanded} {expand} />
      </svelte:component>
    </Lazy>
  {:else}
    <div class="cell-details-panel mc-drawer">
      <div class="scroll">
        <Lazy load={loadStormDetails} let:module>
          <svelte:component this={module.default} selection={$selectedStorm} />
        </Lazy>
      </div>
    </div>
  {/if}
{:else if $selectedStorm && $smallScreen}
  <StormSelectionHint selection={$selectedStorm} />
{:else if $modelCompareAt}
  <!-- The model comparison, opened from the dry-weather strip: the same
       drawer a storm's details take, sheet on a phone and corner panel on a
       desktop. Keyed on the place and range: the panel fetches once, on
       mount. The sheet opens at full height: it is charts to read, and at
       half they were a strip of axis labels. -->
  {#key `${$modelCompareAt.lat},${$modelCompareAt.lon},${$modelCompareAt.hours ?? 24}`}
    <Lazy load={loadCompare($smallScreen)} floating let:module={[{ default: ModelCompare }, sheet]}>
      {#if sheet}
        <svelte:component this={sheet.default} onClose={closeCompare} full>
          <svelte:component
            this={ModelCompare}
            lat={$modelCompareAt.lat}
            lon={$modelCompareAt.lon}
            initialHours={$modelCompareAt.hours ?? 24}
            onClose={closeCompare} />
        </svelte:component>
      {:else}
        <div class="cell-details-panel mc-drawer">
          <div class="scroll">
            <svelte:component
              this={ModelCompare}
              lat={$modelCompareAt.lat}
              lon={$modelCompareAt.lon}
              initialHours={$modelCompareAt.hours ?? 24}
              onClose={closeCompare} />
          </div>
        </div>
      {/if}
    </Lazy>
  {/key}
{/if}

{#if $toolbarVisible === "yes"}
  <NowcastPlayback cap={lm.getCapability("radar") as RadarCapability} />
{/if}
